import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Copy, Eye, EyeOff, Check, ShieldAlert } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import PinPad from "@/components/PinPad";
import { api } from "@/lib/api";

function shuffle(a) {
  return [...a].sort(() => Math.random() - 0.5);
}

export default function CreateWallet() {
  const nav = useNavigate();
  const { newDraft, draftMnemonic, commitWallet } = useWallet();
  const [step, setStep] = useState("length"); // length | seed | verify | pin | confirm | profile
  const [words, setWords] = useState(12);
  const [hidden, setHidden] = useState(true);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");
  const [profile, setProfile] = useState({ name: "", email: "" });

  const wordList = useMemo(() => (draftMnemonic ? draftMnemonic.split(" ") : []), [draftMnemonic]);

  // verify challenge: 3 random positions
  const challenge = useMemo(() => {
    if (!wordList.length) return [];
    const idxs = shuffle(wordList.map((_, i) => i)).slice(0, 3).sort((a, b) => a - b);
    return idxs.map((i) => {
      const others = shuffle(wordList.filter((_, j) => j !== i)).slice(0, 2);
      return { pos: i, correct: wordList[i], options: shuffle([wordList[i], ...others]) };
    });
  }, [wordList]);
  const [picks, setPicks] = useState({});

  const startWith = (n) => {
    setWords(n);
    newDraft(n);
    setStep("seed");
  };

  const copySeed = () => {
    navigator.clipboard.writeText(draftMnemonic);
    toast.success("Seed phrase copied");
  };

  const verifyOk = challenge.length > 0 && challenge.every((c) => picks[c.pos] === c.correct);

  const finish = async () => {
    const acc = commitWallet(draftMnemonic, pin, words);
    if (profile.name && profile.email) {
      try {
        await api.post("/users", { name: profile.name, email: profile.email, tron_address: acc.tron.address });
      } catch {}
    }
    toast.success("Wallet created");
    nav("/portfolio", { replace: true });
  };

  const back = () => {
    if (step === "length") nav("/onboarding");
    else if (step === "seed") setStep("length");
    else if (step === "verify") setStep("seed");
    else if (step === "pin") setStep("verify");
    else if (step === "confirm") { setStep("pin"); setPin(""); }
    else if (step === "profile") setStep("confirm");
  };

  return (
    <div className="cw-shell min-h-screen px-6 py-6">
      <button onClick={back} data-testid="create-back-button" className="mb-6 flex items-center gap-1 text-[#9CA3AF] hover:text-white">
        <ChevronLeft className="h-5 w-5" /> <span className="font-sans text-sm">Back</span>
      </button>

      {step === "length" && (
        <div className="fade-up">
          <h1 className="font-outfit text-3xl font-extrabold">New wallet</h1>
          <p className="mt-2 font-sans text-sm text-[#9CA3AF]">Choose your recovery phrase length.</p>
          <div className="mt-8 space-y-3">
            {[12, 24].map((n) => (
              <button
                key={n}
                data-testid={`seed-length-${n}`}
                onClick={() => startWith(n)}
                className="flex w-full items-center justify-between card-surface p-5 text-left transition-colors hover:border-neon/40"
              >
                <div>
                  <p className="font-outfit text-lg font-bold text-white">{n} words</p>
                  <p className="font-sans text-xs text-[#9CA3AF]">{n === 12 ? "Standard security" : "Maximum security"}</p>
                </div>
                <span className="font-rajdhani text-2xl text-neon">{n === 12 ? "128" : "256"}<span className="text-xs text-[#6B7280]">bit</span></span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === "seed" && (
        <div className="fade-up">
          <h1 className="font-outfit text-3xl font-extrabold">Backup phrase</h1>
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-3">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#F59E0B]" />
            <p className="font-sans text-xs text-[#F59E0B]">Write these words down in order. Anyone with them controls your funds.</p>
          </div>

          <div className="relative mt-6">
            <div className={`grid grid-cols-3 gap-2 ${hidden ? "blur-sm select-none" : ""}`}>
              {wordList.map((w, i) => (
                <div key={i} data-testid="seed-phrase-word-chip" className="flex items-center gap-1.5 rounded-xl border border-[#1F2430] bg-[#12141A] px-2.5 py-2.5">
                  <span className="font-rajdhani text-xs text-[#6B7280]">{i + 1}</span>
                  <span className="font-rajdhani text-sm font-semibold text-white">{w}</span>
                </div>
              ))}
            </div>
            {hidden && (
              <button onClick={() => setHidden(false)} className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center gap-2 rounded-full border border-neon/40 bg-[#12141A] px-4 py-2 font-sans text-sm text-neon">
                  <Eye className="h-4 w-4" /> Tap to reveal
                </span>
              </button>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button onClick={() => setHidden((h) => !h)} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#1F2430] py-3 font-sans text-sm text-[#9CA3AF] hover:text-white">
              {hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} {hidden ? "Show" : "Hide"}
            </button>
            <button data-testid="copy-seed-button" onClick={copySeed} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#1F2430] py-3 font-sans text-sm text-[#9CA3AF] hover:text-white">
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>

          <button
            data-testid="seed-backed-up-button"
            onClick={() => { setPicks({}); setStep("verify"); }}
            className="mt-6 w-full rounded-2xl bg-neon py-4 font-outfit font-bold text-[#08090C] active:scale-[0.98] neon-glow"
          >
            I've written it down
          </button>
        </div>
      )}

      {step === "verify" && (
        <div className="fade-up">
          <h1 className="font-outfit text-3xl font-extrabold">Verify backup</h1>
          <p className="mt-2 font-sans text-sm text-[#9CA3AF]">Select the correct word for each position.</p>
          <div className="mt-8 space-y-6">
            {challenge.map((c) => (
              <div key={c.pos}>
                <p className="mb-2 font-rajdhani text-sm text-[#9CA3AF]">Word #{c.pos + 1}</p>
                <div className="grid grid-cols-3 gap-2">
                  {c.options.map((o) => (
                    <button
                      key={o}
                      data-testid="verify-seed-word-button"
                      onClick={() => setPicks((p) => ({ ...p, [c.pos]: o }))}
                      className={`rounded-xl border py-3 font-rajdhani text-sm font-semibold transition-colors ${
                        picks[c.pos] === o ? "border-neon bg-neon/10 text-neon" : "border-[#1F2430] bg-[#12141A] text-white hover:border-neon/30"
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            data-testid="verify-continue-button"
            disabled={!verifyOk}
            onClick={() => setStep("pin")}
            className="mt-8 w-full rounded-2xl bg-neon py-4 font-outfit font-bold text-[#08090C] transition-opacity disabled:opacity-30 active:scale-[0.98]"
          >
            Continue
          </button>
        </div>
      )}

      {step === "pin" && (
        <div className="fade-up flex flex-col items-center">
          <h1 className="font-outfit text-3xl font-extrabold">Set a PIN</h1>
          <p className="mb-10 mt-2 font-sans text-sm text-[#9CA3AF]">Encrypts your wallet on this device.</p>
          <PinPad onComplete={(p) => { setPin(p); setStep("confirm"); }} />
        </div>
      )}

      {step === "confirm" && (
        <div className="fade-up flex flex-col items-center">
          <h1 className="font-outfit text-3xl font-extrabold">Confirm PIN</h1>
          <p className="mb-10 mt-2 font-sans text-sm text-[#9CA3AF]">Re-enter your PIN.</p>
          <PinPad
            error={pinErr}
            onComplete={(p) => {
              if (p !== pin) { setPinErr("PINs don't match, try again"); setStep("pin"); setPin(""); return; }
              setPinErr("");
              setStep("profile");
            }}
          />
        </div>
      )}

      {step === "profile" && (
        <div className="fade-up">
          <h1 className="font-outfit text-3xl font-extrabold">Almost there</h1>
          <p className="mt-2 font-sans text-sm text-[#9CA3AF]">Optionally add a profile (name &amp; email). Only these — never your keys — are stored.</p>
          <div className="mt-8 space-y-4">
            <Field label="Name" value={profile.name} onChange={(v) => setProfile((s) => ({ ...s, name: v }))} testid="profile-name-input" />
            <Field label="Email" value={profile.email} onChange={(v) => setProfile((s) => ({ ...s, email: v }))} testid="profile-email-input" />
          </div>
          <button data-testid="finish-create-button" onClick={finish} className="mt-8 w-full rounded-2xl bg-neon py-4 font-outfit font-bold text-[#08090C] active:scale-[0.98] neon-glow">
            <span className="flex items-center justify-center gap-2"><Check className="h-5 w-5" /> Enter wallet</span>
          </button>
          <button onClick={finish} className="mt-3 w-full py-2 font-sans text-sm text-[#6B7280] hover:text-white">Skip for now</button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1.5 block font-sans text-xs text-[#9CA3AF]">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-[#1F2430] bg-[#12141A] px-4 py-3 font-sans text-sm text-white outline-none focus:border-neon/50"
      />
    </div>
  );
}
